import React, { useCallback, useMemo } from 'react';
import { injectIntl } from 'react-intl';

import useForm from 'utils/useForm';
import CustomInput from 'components/CustomInput';
import PermissionLogo from 'components/PermissionLogo';
import { addAdminValidation as validation } from './validation';

import {
  Col,
  Row,
  Modal,
  Tabs,
  Form,
  Checkbox,
} from 'antd';

import './style.less';

const BOOLEAN_PERMISSIONS = {
  community: [
    'can_read_initiatives',
    'can_read_reports',
    'can_manage_participation',
    'can_manage_collaborations',
    'can_manage_proposals',
    'can_manage_initiatives',
    'can_manage_volunteers',
  ],
  general: [
    'can_manage_configuration',
    'can_manage_permissions',
  ],
};

const MANDATORY_PERMISSIONS = {
  community: ['can_read_initiatives'],
  general: [],
};

const AddAdmin = ({
  intl,
  visible,
  pushing,
  onSubmit,
  onCancel,
  organization,
  allowedProducts,
  currentUser,
  is_system
}) => {
  const t = intl.messages;

  const validateForm = useMemo(() =>
    validation(intl),
    [ intl ]
  );
  const defaults = {
    general_permission: {},
    community_permission: allowedProducts.includes('community')
      ? { can_read_initiatives: true }
      : {},
  };
  const submitForm = () => {
    onSubmit(values);
    onCancel();
    resetForm();
  };

  const {
    values,
    handleChange,
    handleChangeEvent,
    handleSubmit,
    isDirty,
    errors,
    resetForm,
  } = useForm({
    callback: submitForm,
    validate: validateForm,
    defaultValues: defaults,
  });
  const showError = useCallback(
    (name) => (!isDirty(name) && errors[name]) || null,
    [isDirty, errors]
  );

  const togglePermission = useCallback(
    (app, permission) => {
      let newState = {
        ...values[`${app}_permission`],
        [ permission ]: !values[`${app}_permission`][permission]
      };

      handleChange(`${app}_permission`)(newState);
    },
    [values, handleChange]
  );

  const permissionDisabled = useCallback((app, permission) => {
    const hasPermission =  MANDATORY_PERMISSIONS[app].includes(permission);
    return is_system ? 
      hasPermission : 
      (hasPermission || !((currentUser[`${app}_permission_inherit`] || {})[permission] || currentUser[`${app}_permission`][permission]));
  }, [currentUser, is_system]);

  const mandatoryPermissions = useMemo(
    () => {
      let permissions = MANDATORY_PERMISSIONS;
      if (values.details?.is_auditor) {
        return {
          community: [],
          general: [],
        };
      }
      return permissions;
    }, [
      values,
    ]
  );

  const disabledPermissions = useMemo(
    () => {
      if (values.details?.is_auditor) {
        return {
          community: [
            'can_read_initiatives',
            'can_read_reports',
            'can_manage_participation',
            'can_manage_collaborations',
            'can_manage_proposals',
            'can_manage_initiatives',
            'can_manage_volunteers',
          ],
          general: [
            'can_manage_configuration',
            'can_manage_permissions',
          ],
        };
      }
      return {};
    },
    [
      values.details,
    ]
  );

  return (
    <Modal
      visible={visible}
      onCancel={onCancel}
      onOk={handleSubmit}
      okText={t.organization_profile_save}
      title={t.organization_profile_admins_add}
      className="AddAdmin"
      okButtonProps={{ disabled: pushing }}
      cancelButtonProps={{ disabled: pushing }}
      closable={!pushing}
    >
      <Row>
        <Col span={24} className="AddAdmin-label">
          { t.organization_profile_name }
        </Col>
        <Col span={24}>
          <Form.Item
            hasFeedback
            validateStatus={showError('name') ? 'error' : ''}
            help={showError('name')}
            colon={false}
          >
            <CustomInput
              name="name"
              value={values.name}
              onChange={handleChangeEvent}
              placeholder={t.organization_profile_name}
            />
          </Form.Item>
        </Col>
      </Row>
      <Row>
        <Col span={24} className="AddAdmin-label">
          { t.organization_profile_email }
        </Col>
        <Col span={24}>
          <Form.Item
            hasFeedback
            validateStatus={showError('email') ? 'error' : ''}
            help={showError('email')}
            colon={false}
          >
            <CustomInput
              name="email"
              value={values.email}
              onChange={handleChangeEvent}
              placeholder={t.organization_profile_email}
            />
          </Form.Item>
        </Col>
      </Row>
      <Row>
        <Col span={24} className="AddAdmin-label">
          { t.organization_profile_permissions }
        </Col>
        <Col span={24}>
          <Tabs>
            <Tabs.TabPane
              tab={ t.organization_profile_permissions_general }
              key="general"
            >
              <Row gutter={10}>
                {
                  BOOLEAN_PERMISSIONS.general.map(permission => (
                    <Col key={permission} span={24}>
                      <Checkbox
                        checked={values.general_permission[permission]}
                        onChange={() => togglePermission('general', permission)}
                        disabled={ mandatoryPermissions.general.includes(permission) || disabledPermissions.general?.includes(permission) || permissionDisabled('general', permission) }
                      >
                        <PermissionLogo
                          className="AddAdmin-permission-logo"
                          permission={permission}
                          app="general"
                        />
                        { t[`permission_general_${permission}`] }
                      </Checkbox>
                    </Col>
                  ))
                }
              </Row>
            </Tabs.TabPane>
            { allowedProducts.includes('community')
              ? <Tabs.TabPane
                  tab={ t.organization_profile_permissions_community }
                  key="community"
                >
                  <Row gutter={10}>
                    {
                      BOOLEAN_PERMISSIONS.community.map(permission => (
                        <Col key={permission} span={24}>
                          <Checkbox
                            checked={values.community_permission[permission]}
                            onChange={() => togglePermission('community', permission)}
                            disabled={ mandatoryPermissions.community.includes(permission) || disabledPermissions.community?.includes(permission) || permissionDisabled('community', permission) }
                          >
                            <PermissionLogo
                              className="AddAdmin-permission-logo"
                              permission={permission}
                              app="community"
                            />
                            { t[`permission_community_${permission}`] }
                          </Checkbox>
                        </Col>
                      ))
                    }
                  </Row>
                </Tabs.TabPane>
              : null
            }
          </Tabs>
        </Col>
      </Row>
    </Modal>
  )
}

export default injectIntl(AddAdmin);
